import React, { useState, useEffect } from 'react';
import { Form, Modal, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Filter from '../../../shared/components/filter';
import * as actions from './../store/actions';
import { filterTypes } from '../../../shared/constants/filterType';
import { Note, Task } from '../../../shared/models';
import { RootState } from '../../../store';

export type AssignNoteToTaskModalProps = {
  visible: boolean;
  note: Note;
  insuredId: string | null | undefined;
  close: () => void;
  refresh?: () => void;
};

interface FormData {
  taskId: string;
  noteId?: string;
  insuredId?: string | null | undefined;
}

const AssignNoteToTaskModal: React.FC<AssignNoteToTaskModalProps> = ({ visible, note, insuredId, close, refresh }) => {
  const dispatch = useDispatch();

  const [assignNoteToTaskForm] = Form.useForm<FormData>();

  const assignNoteToTaskLoading = useSelector((state: RootState) => state.notes.assignNoteToTaskLoading);
  const tasks = useSelector((state: RootState) => state.noteTypes.tasks);
  const tasksLoading = useSelector((state: RootState) => state.noteTypes.tasksLoading);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    if (note.ticketId) {
      const task: Task = { id: note.ticketId, ticketTitle: note.ticketTitle || '' } as Task;
      setSelectedTask(task);
      assignNoteToTaskForm.setFieldsValue({ taskId: note.ticketId });
      dispatch(actions.getTasks(insuredId, note.ticketTitle || '') as any);
    }
  }, [note, assignNoteToTaskForm, dispatch, insuredId]);

  const assign = (): void => {
    assignNoteToTaskForm.validateFields().then((values: FormData) => {
      const formData: FormData = values;
      formData.noteId = note.id;
      formData.insuredId = insuredId;

      if (note.ticketId && formData.taskId === note.ticketId) {
        clearForm();
        close();
      } else {
        dispatch(actions.assignNoteToTask(formData) as any).then(() => {
          clearForm();
          if (refresh) refresh();
          close();
        });
      }
    });
  };

  const clearForm = (): void => {
    assignNoteToTaskForm.resetFields();
  };

  const cancel = (): void => {
    clearForm();
    close();
  };

  return (
    <Modal
      title="Assign note to Task"
      centered
      open={visible}
      onCancel={() => cancel()}
      onOk={() => assign()}
      width={300}
      maskClosable={false}
      footer={[
        <Button key="back" onClick={() => cancel()}>
          Cancel
        </Button>,
        <Button key="button" type="primary" onClick={() => assign()} loading={assignNoteToTaskLoading} disabled={assignNoteToTaskLoading}>
          Assign
        </Button>,
      ]}
    >
      <Form
        id="assign-note-to-task-modal"
        form={assignNoteToTaskForm}
        style={{ width: '100%' }}
        layout="horizontal"
        size="small"
        labelAlign="left"
        initialValues={{ taskId: selectedTask?.id }}
      >
        <Filter
          rules={[{ required: true, message: 'This field is required.' }]}
          filterType={filterTypes.dropdownWithServerData}
          filterByChildData={true}
          name={'taskId'}
          label={'Task'}
          mode="single"
          placeholder="Type and/or choose task"
          data={selectedTask?.id ? [selectedTask, ...(tasks || [])] : (tasks || [])}
          dataLoading={tasksLoading}
          dataSearchAction={(searchString: string | undefined) => actions.getTasks(insuredId, searchString)}
          keyPropertyName={'id'}
          valuePropertyName={'id'}
          textPropertyName={'ticketTitle'}
          titlePropertyName={'ticketTitle'}
        />
      </Form>
    </Modal>
  );
};

export default AssignNoteToTaskModal;
